<template>
  <v-dialog v-model="resultsDialog" width="1000">
    <template v-slot:activator="{ on }">
      <v-btn
        text
        outlined
        color="blue darken-1"
        @click="resultsDialog = true"
        v-on="on"
        >Results API path</v-btn
      >
    </template>
    <v-card>
      <v-toolbar class="grey-text">
        <icon scale="1.5" class="mr-2" name="copy" />
        <v-toolbar-title>
          Generate results path for scripting api</v-toolbar-title
        >
        <v-spacer></v-spacer>
        <v-btn icon small class="m-0" @click="resultsDialog = false">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="mt-3">
        Select the metric you want and click "COPY PATH" to copy the path to the
        metric to your clipboard
      </v-card-text>

      <v-row no-gutters class="mx-6">
        <v-col cols="6" class="dialog-scrolling-col pr-2">
          <v-treeview
            :items="items"
            activatable
            :active.sync="activeItems"
            expand-icon="expand_more"
            :open.sync="openItems"
            return-object
          >
            <template v-slot:label="{ item }">
              <p class="my-1 grey-text">
                {{ item.label }}
              </p>
            </template>
          </v-treeview>
        </v-col>
        <v-divider vertical></v-divider>
        <v-col class="dialog-scrolling-col pl-2">
          <v-card-text>
            <v-card-title class="pt-0">
              Path for python scripting api
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  {{ apiPathToCopy }}
                </v-col>
                <v-col>
                  <v-btn
                    class="white--text"
                    color="primary"
                    depressed
                    @click.stop="copyTextToClipboard(apiPathToCopy)"
                    :disabled="!apiPathToCopy.length > 0"
                  >
                    <icon scale="1.5" class="pr-2" name="copy" />
                    Copy path
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-title> Preview value </v-card-title>
            <v-row>
              <pre
                >{{ activeItemValueString }}
              </pre>
            </v-row>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import plotInput from '../../mixins/plotInput.js'
import errorMixin from '../../mixins/logHandler.js'
import { mapGetters } from 'vuex'

export default {
  name: 'results-api-script',
  mixins: [plotInput, errorMixin],

  props: {
    results: {
      type: Object | Array,
      required: true
    }
  },

  data() {
    return {
      resultsNodeName: 'results',
      resultsDialog: false,
      activeItems: [],
      openItems: []
    }
  },

  mounted() {
    this.activeItems.push(this.items[0])
  },

  methods: {
    async copyTextToClipboard(text) {
      if (text) {
        await navigator.clipboard.writeText(text)
        this.displayMessage(
          'Text copied to clipboard',
          '',
          'notification',
          'success',
          3000
        )
      }
    },

    buildResultNode(propertyName, propertyValue, parentApiPath, isInArray) {
      const currentApiPath = isInArray
        ? parentApiPath + '[' + propertyName + ']'
        : parentApiPath + '["' + propertyName + '"]'
      const node = {
        id: currentApiPath,
        label: propertyName,
        apiPath: currentApiPath,
        valueString: JSON.stringify(propertyValue, null, 2)
      }

      if (typeof propertyValue === 'object' && propertyValue != null) {
        node['children'] = []
        for (const [key, value] of Object.entries(propertyValue)) {
          node['children'].push(
            this.buildResultNode(
              key,
              value,
              currentApiPath,
              Array.isArray(propertyValue)
            )
          )
        }
      }

      return node
    },

    buildEphBranch() {
      const node = {
        id: 'ephemerides',
        label: 'Ephemerides',
        apiPath: '',
        valueString: ''
      }

      const children = this.listTreeOptions(
        this.results.ephemerides,
        'SimulationResult',
        this.results.fieldIndexes
      )
      for (const node of children) {
        this.addPathAndPreviewToEphs(node)
      }

      node['children'] = children

      return node
    },

    addPathAndPreviewToEphs(node) {
      const nodePath = node.id.replace('.', '')
      if (!node.children) {
        const ephIndex = this.results.fieldIndexes[nodePath]
        const valuesArray = []
        for (const step of this.results.ephemerides) {
          valuesArray.push(step[ephIndex])
        }
        node.valueString = JSON.stringify(valuesArray, null, 2)
        node.apiPath = '["results"]["ephemerides"][:][' + ephIndex + ']'
      } else {
        node.valueString = ''
        node.apiPath = ''
        for (const child of node.children) [this.addPathAndPreviewToEphs(child)]
      }
    }
  },

  computed: {
    ...mapGetters(['currentMission']),

    apiPathToCopy() {
      return this.activeItems[0] ? this.activeItems[0].apiPath : ''
    },

    activeItemValueString() {
      return this.activeItems[0] ? this.activeItems[0].valueString : ''
    },

    items() {
      const treeItems = []
      const resBranch = this.buildResultNode(
        this.resultsNodeName,
        this.results,
        '',
        false
      )
      resBranch.label = 'Raw results'
      treeItems.push(resBranch)

      if (
        this.isNumericalMission &&
        this.results.ephemerides &&
        this.results.fieldIndexes
      ) {
        const ephBranch = this.buildEphBranch()
        treeItems.push(ephBranch)
      }

      return treeItems
    },

    isNumericalMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      )
    }
  }
}
</script>

<style scoped lang="scss">
.dialog-scrolling-col {
  overflow: auto;
  max-height: 70vh;
}

.v-treeview-node__label {
  overflow: initial;
  text-overflow: initial;
}

.v-navigation-drawer__content {
  overflow-x: auto;
}

.v-treeview {
  width: max-content;
}
</style>
